/**
 * API Service - Centralized API calls for the Broker Manager
 * All endpoints return promises and handle errors consistently
 */

import axios from 'axios';
import { toast } from 'sonner';
import { routeStaffManagerRequest } from './staffManagerTransport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  if (window.location.pathname.startsWith('/admin/manager')) return routeStaffManagerRequest(config);
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/admin/manager')) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// ==================== CLIENTS ====================
export const clientsAPI = {
  getAll: async () => {
    const response = await api.get('/clients');
    return response.data;
  },
  getById: async (clientId) => {
    const response = await api.get(`/clients/${clientId}`);
    return response.data;
  },
  create: async (clientData) => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },
  update: async (clientId, updates) => {
    const response = await api.put(`/clients/${clientId}`, updates);
    return response.data;
  },
  updateKYC: async (clientId, kycData) => {
    const response = await api.put(`/clients/${clientId}/kyc`, kycData);
    return response.data;
  },
  getLiveFinancials: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/live-financials`);
    return response.data;
  },
  delete: async (clientId, force = false) => {
    const response = await api.delete(`/clients/${clientId}`, { params: { force } });
    return response.data;
  },
};

// ==================== POSITIONS ====================
export const positionsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/positions', { params });
    return response.data;
  },
  getByClient: async (clientId) => {
    const response = await api.get('/positions', { params: { client_id: clientId } });
    return response.data;
  },
  close: async (positionId, closeData = {}) => {
    const response = await api.put(`/positions/${positionId}/close`, closeData);
    return response.data;
  },
  update: async (positionId, updates) => {
    const response = await api.put(`/positions/${positionId}`, updates);
    return response.data;
  },
  delete: async (positionId) => {
    const response = await api.delete(`/positions/${positionId}`);
    return response.data;
  },
};

// ==================== ORDERS ====================
export const ordersAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },
  create: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },
  cancel: async (orderId) => {
    const response = await api.put(`/orders/${orderId}/cancel`);
    return response.data;
  },
};

// ==================== INSTRUMENTS ====================
export const instrumentsAPI = {
  getAll: async () => {
    const response = await api.get('/instruments');
    return response.data;
  },
  getById: async (instrumentId) => {
    const response = await api.get(`/instruments/${instrumentId}`);
    return response.data;
  },
  create: async (instrumentData) => {
    const response = await api.post('/instruments', instrumentData);
    return response.data;
  },
  update: async (instrumentId, updates) => {
    const response = await api.put(`/instruments/${instrumentId}`, updates);
    return response.data;
  },
  delete: async (instrumentId) => {
    const response = await api.delete(`/instruments/${instrumentId}`);
    return response.data;
  },
  // Bulk update for multiple instruments
  bulkUpdate: async (instrumentIds, updates) => {
    const promises = instrumentIds.map(id => 
      api.put(`/instruments/${id}`, updates)
    );
    const results = await Promise.all(promises);
    return results.map(r => r.data);
  },
  // Virtual instrument specific
  getVirtualPrice: async (instrumentId) => {
    const response = await api.get(`/instruments/${instrumentId}/price`);
    return response.data;
  },
  getVirtualChart: async (instrumentId, periods = 100) => {
    const response = await api.get(`/instruments/${instrumentId}/chart`, { params: { periods } });
    return response.data;
  },
};

// ==================== TRANSACTIONS ====================
export const transactionsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/transactions', { params });
    return response.data;
  },
  getByClient: async (clientId) => {
    const response = await api.get('/transactions', { params: { client_id: clientId } });
    return response.data;
  },
  create: async (transactionData) => {
    const response = await api.post('/transactions', transactionData);
    return response.data;
  },
  process: async (transactionId, status) => {
    const response = await api.put(`/transactions/${transactionId}/process`, { status });
    return response.data;
  },
};

// ==================== MARKET DATA ====================
export const marketAPI = {
  getCrypto: async () => {
    const response = await api.get('/market/crypto');
    return response.data;
  },
  getCryptoList: async () => {
    const response = await api.get('/market/crypto/list');
    return response.data;
  },
  getForex: async () => {
    const response = await api.get('/market/forex');
    return response.data;
  },
  getBISTStocks: async () => {
    const response = await api.get('/market/stocks/bist');
    return response.data;
  },
};

// ==================== DASHBOARD ====================
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
  getRecentActivity: async (limit = 10) => {
    const response = await api.get('/dashboard/recent-activity', { params: { limit } });
    return response.data;
  },
};

// ==================== RISK ====================
export const riskAPI = {
  getExposure: async () => {
    const response = await api.get('/risk/exposure');
    return response.data;
  },
  getMarginAlerts: async () => {
    const response = await api.get('/risk/margin-alerts');
    return response.data;
  },
};

// ==================== TRADING TERMS ====================
// Note: Backend doesn't have trading terms endpoints yet - will use instruments for now
export const tradingTermsAPI = {
  // These would be added to backend
  getAll: async () => {
    // Placeholder - return mock data for now
    return [];
  },
  create: async (termData) => {
    // Placeholder
    return termData;
  },
  update: async (termId, updates) => {
    // Placeholder
    return updates;
  },
};

// ==================== TRADING SESSIONS ====================
export const sessionsAPI = {
  getStatus: async () => {
    const response = await api.get('/sessions/status');
    return response.data;
  },
  checkInstrument: async (symbol) => {
    const response = await api.get(`/sessions/check/${symbol}`);
    return response.data;
  },
  haltMarket: async (market, reason) => {
    const response = await api.post('/sessions/halt-market', { market, reason });
    return response.data;
  },
  resumeMarket: async (market) => {
    const response = await api.post('/sessions/resume-market', { market });
    return response.data;
  },
  haltInstrument: async (symbol, reason) => {
    const response = await api.post('/sessions/halt-instrument', { symbol, reason });
    return response.data;
  },
  resumeInstrument: async (symbol) => {
    const response = await api.post('/sessions/resume-instrument', { symbol });
    return response.data;
  },
};

// ==================== HOLIDAYS ====================
export const holidaysAPI = {
  getAll: async () => {
    const response = await api.get('/holidays');
    return response.data;
  },
  create: async (holidayData) => {
    const response = await api.post('/holidays', holidayData);
    return response.data;
  },
  delete: async (holidayId) => {
    const response = await api.delete(`/holidays/${holidayId}`);
    return response.data;
  },
};

// ==================== SEED DATA ====================
export const seedAPI = {
  seed: async () => {
    const response = await api.post('/seed');
    return response.data;
  },
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Wrapper for API calls with toast notifications
 */
export const withToast = async (apiCall, successMsg, errorMsg) => {
  try {
    const result = await apiCall();
    if (successMsg) toast.success(successMsg);
    return { success: true, data: result };
  } catch (error) {
    const message = error.response?.data?.detail || error.message || errorMsg || 'An error occurred';
    toast.error(message);
    return { success: false, error: message };
  }
};

/**
 * Batch API calls with progress
 */
export const batchAPICall = async (items, apiCallFn, progressCallback) => {
  const results = [];
  for (let i = 0; i < items.length; i++) {
    try {
      const result = await apiCallFn(items[i]);
      results.push({ success: true, data: result, item: items[i] });
    } catch (error) {
      results.push({ success: false, error: error.message, item: items[i] });
    }
    if (progressCallback) {
      progressCallback((i + 1) / items.length * 100);
    }
  }
  return results;
};

// Export the base api instance for custom calls
export { api };
export default api;
